<template>
  <b-modal
    :title="campaignTitle"
    id="create-campaign-modal"
    size="lg"
    hide-footer
    no-close-on-backdrop
  >
    <form-wizard
      color="#7367F0"
      :title="null"
      :subtitle="null"
      shape="tab"
      finish-button-text="Done"
      back-button-text="< Back"
      next-button-text="Next >"
      @on-complete="formSubmitted"
    >
      <!-- Step 1: Goal tab -->
      <tab-content title="Goal" :before-change="inputGoal">
        <b-row>
          <b-col cols="12" class="mb-2">
            <h3 class="mb-0">Step 1</h3>
            <span class="text-muted"> Choose a goal </span>
          </b-col>
          <b-col md="12">
            <b-card>
              <h5 class="mb-1">Campaign Goal</h5>
              <b-row>
                <b-col md="6">
                  <b-form-radio
                    v-model="newCampaignData.goal"
                    name="goal-radios"
                    value="CPM"
                    class="mb-1 ml-1"
                  >
                    CPM
                  </b-form-radio>
                  <p class="ml-4">Cost per 1,000 impression</p>
                </b-col>
                <b-col md="6">
                  <b-form-radio
                    v-model="newCampaignData.goal"
                    name="goal-radios"
                    value="CPC"
                    class="mb-1 ml-1"
                  >
                    CPC
                  </b-form-radio>
                  <p class="ml-4">Cost per click</p>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
          <b-col md="12">
            <b-card>
              <validation-observer ref="goalRules" tag="form">
                <h5 class="mb-1">Additional Setting</h5>
                <b-form-checkbox
                  v-model="newCampaignData.includeLat"
                  class="custom-control-primary ml-1 mb-1"
                >
                  Include LAT
                  <small class="text-muted ml-1">Limited Ad Tracking</small>
                </b-form-checkbox>
                <validation-provider
                  #default="{ errors }"
                  name="Proportion"
                  rules="required|numberCheckRule"
                >
                  <div class="d-flex align-items-center">
                    <span class="ml-1">Proportion</span>
                    <b-input-group
                      class="input-group-merge ml-1"
                      :class="{
                        'is-invalid':
                          errors.length > 0 && newCampaignData.includeLat,
                      }"
                    >
                      <b-form-input
                        v-model="newCampaignData.includeLatProportion"
                        placeholder="100"
                        :state="
                          (newCampaignData.includeLat && errors.length) > 0
                            ? false
                            : null
                        "
                        :disabled="!newCampaignData.includeLat"
                        type="number"
                        @keypress="checkNumber"
                      />
                      <b-input-group-append is-text>
                        <feather-icon icon="PercentIcon" />
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                  <small
                    class="text-danger ml-1"
                    v-if="newCampaignData.includeLat"
                  >
                    {{ errors[0] }}
                  </small>
                </validation-provider>
              </validation-observer>
            </b-card>
          </b-col>
        </b-row>
      </tab-content>

      <!-- Step 2: Basic Info tab -->
      <tab-content title="Basic Info" :before-change="inputBasicInfo">
        <h3 class="mb-0">Step 2</h3>
        <span class="text-muted">Set Basic Info</span>
        <validation-observer ref="infoRules" tag="form">
          <b-card class="mt-1">
            <b-row>
              <b-col md="12">
                <h5 class="mb-1">Basic Information</h5>
                <b-form-group label-for="bi-title">
                  Title<span class="text-danger"> *</span>
                  <validation-provider
                    #default="{ errors }"
                    name="Title"
                    rules="required"
                  >
                    <b-form-textarea
                      id="bi-title"
                      v-model="newCampaignData.title"
                      placeholder="Campagin Title"
                      :state="errors.length > 0 ? false : null"
                      maxlength="50"
                      :disabled="mode === 'edit'"
                    />
                    <small
                      class="textarea-counter-value float-right"
                      :class="{
                        'bg-danger': newCampaignData.title.length > titleLength,
                      }"
                    >
                      <span class="char-count">
                        {{ newCampaignData.title.length }}
                      </span>
                      / {{ titleLength }}
                    </small>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group label-for="bi-description">
                  Description
                  <b-form-textarea
                    id="bi-description"
                    v-model="newCampaignData.description"
                    placeholder="Campaign for Demonstraition"
                    maxLength="200"
                  />
                  <small
                    class="textarea-counter-value float-right"
                    :class="{
                      'bg-danger':
                        newCampaignData.description.length > descriptionLength,
                    }"
                  >
                    <span class="char-count">
                      {{ newCampaignData.description.length }}
                    </span>
                    / {{ descriptionLength }}
                  </small>
                </b-form-group>
              </b-col>
              <b-col md="12">
                <validation-provider
                  #default="{ errors }"
                  name="Country"
                  rules="required"
                >
                  <b-form-group
                    label-for="bi-country"
                    :state="errors.length > 0 ? false : null"
                    :disabled="mode === 'edit'"
                  >
                    Target Country<span class="text-danger"> *</span>
                    <v-select
                      id="bi-country"
                      v-model="newCampaignData.targetCountry"
                      :options="countries"
                      :reduce="(option) => option.value"
                      label="text"
                      :disabled="mode === 'edit'"
                    />
                    <b-form-invalid-feedback
                      :state="errors.length > 0 ? false : null"
                    >
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col md="12">
                <b-form-group label-for="bi-domain">
                  Advertiser Domain<span class="text-danger"> *</span>
                  <validation-provider
                    #default="{ errors }"
                    name="Domain"
                    rules="required"
                  >
                    <b-form-input
                      id="bi-domain"
                      v-model="newCampaignData.advertiserDomain"
                      :state="errors.length > 0 ? false : null"
                      placeholder="dexflux.com"
                      maxlength="100"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>

          <b-card>
            <b-col md="12">
              <h5 class="mb-1">
                App Information<small class="ml-1">(optional)</small>
              </h5>
              <b-form-checkbox
                v-model="newCampaignData.app"
                name="check-app"
                class="mb-1"
              >
                App
              </b-form-checkbox>
            </b-col>
            <b-col md="12">
              <validation-provider
                #default="{ errors }"
                name="Android or IOS Bundle ID"
                rules="required|appCheckRule:@android,@ios"
                :disabled="!newCampaignData.app"
              >
                App Bundle ID
                <div class="d-flex align-items-center">
                  <span class="ml-1" style="width: 100px">Android</span>
                  <b-form-input
                    id="bi-android"
                    v-model="newCampaignData.appAndroid"
                    maxlength="100"
                    class="ml-1"
                    :state="
                      newCampaignData.app && errors.length > 0
                        ? false
                        : null
                    "
                    :disabled="!newCampaignData.app"
                  />
                </div>

                <div class="d-flex align-items-center mt-1">
                  <span class="ml-1" style="width: 100px">IOS</span>
                  <b-form-input
                    id="bi-ios"
                    v-model="newCampaignData.appIos"
                    maxlength="100"
                    class="ml-1"
                    :state="
                      newCampaignData.app && errors.length > 0
                        ? false
                        : null
                    "
                    :disabled="!newCampaignData.app"
                  />
                </div>
                <small class="text-danger" v-if="newCampaignData.app">
                  {{ errors[0] }}
                </small>
              </validation-provider>

              <b-form-group label-for="bi-trklink" class="mt-1">
                Tracking Link<small class="ml-1">(optional)</small>
                <v-select
                  id="bi-trklink"
                  v-model="newCampaignData.trkLinkSq"
                  label="trkLinkTitle"
                  placeholder="Select..."
                  :options="mmpTrkLinks"
                  :reduce="(option) => option.trkLinkSq"
                  :disabled="!newCampaignData.app"
                  return-object
                />
              </b-form-group>

              <validation-provider
                #default="{ errors }"
                name="camp-level-param"
                :disabled="newCampaignData.trkLinkSq == null"
              >
                <div class="mt-1">
                  Campaign Level Parameter
                  <div class="d-flex align-items-center">
                    <b-form-input
                      id="bi-camp-level-param"
                      v-model="newCampaignData.trkLinkCampLvParam"
                      placeholder="Campaign Title"
                      maxlength="50"
                      :state="errors.length > 0 ? false : null"
                      :disabled="newCampaignData.trkLinkSq == null"
                    />
                  </div>
                  <small
                    class="textarea-counter-value float-right"
                    :class="{
                      'bg-danger': newCampaignData.trkLinkCampLvParam.length > titleLength,
                    }"
                  >
                    <span class="char-count">
                      {{ newCampaignData.trkLinkCampLvParam.length }}
                    </span>
                    / {{ titleLength }}
                  </small>
                  <small class="text-danger">{{ errors[0] }}</small>
                </div>
              </validation-provider>
            </b-col>
          </b-card>
        </validation-observer>
      </tab-content>

      <!-- Step 3: Schedule & Budget tab  -->
      <tab-content title="Schedule & Budget" :before-change="inputSchedule">
        <validation-observer ref="scheduleRules" tag="form">
          <b-row>
            <b-col cols="12" class="mb-2">
              <h3 class="mb-0">Step 3</h3>
              <span class="text-muted">Schedule & Budget</span>
            </b-col>
            <b-col md="12">
              <b-card>
                <h5 class="mb-1">Schedule</h5>
                <b-row>
                  <b-col md="6">
                    <validation-provider
                      #default="{ errors }"
                      name="Start Date"
                      rules="required"
                    >
                      <b-form-group label-for="start-date">
                        Start Date<span class="text-danger"> *</span>
                        <b-input-group class="input-group-merge">
                          <b-input-group-prepend is-text>
                            <feather-icon icon="CalendarIcon" />
                          </b-input-group-prepend>
                          <flat-pickr
                            v-model="newCampaignData.scheduleStartDate"
                            id="start_date"
                            :config="startDateConfig"
                            class="form-control flat-picker"
                            placeholder="Mon DD, YYYY"
                          />
                        </b-input-group>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                  <b-col md="6">
                    <b-form-checkbox
                      v-model="newCampaignData.noEndDate"
                      class="custom-control-primary float-right mb-0"
                    >
                      No End Date
                    </b-form-checkbox>
                    <validation-provider
                      #default="{ errors }"
                      name="End Date"
                      rules="required"
                      :disabled="newCampaignData.noEndDate"
                    >
                      <b-form-group label-for="end-date">
                        End Date
                        <b-input-group
                          class="input-group-merge"
                          :class="{
                            'bg-transparent': !newCampaignData.noEndDate,
                          }"
                          style="background-color: #efefef"
                        >
                          <b-input-group-prepend is-text>
                            <feather-icon icon="CalendarIcon" />
                          </b-input-group-prepend>
                          <flat-pickr
                            v-model="newCampaignData.scheduleEndDate"
                            :config="endDateConfig"
                            id="end-date"
                            class="form-control flat-picker"
                            placeholder="Mon DD, YYYY"
                            :disabled="newCampaignData.noEndDate"
                          />
                        </b-input-group>
                        <small
                          class="text-danger"
                          v-if="!newCampaignData.noEndDate"
                        >
                          {{ errors[0] }}
                        </small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <h5 class="mt-1 mb-1">Budget</h5>
                <b-row>
                  <b-col md="2">
                    <b-form-radio
                      v-model="newCampaignData.budgetType"
                      name="repeat-radios"
                      value="DAILY"
                      @change="calculateTotal"
                    >
                      Daily
                    </b-form-radio>
                  </b-col>
                  <b-col md="2">
                    <b-form-radio
                      v-model="newCampaignData.budgetType"
                      name="repeat-radios"
                      value="WEEKLY"
                      @change="calculateTotal"
                    >
                      Weekly
                    </b-form-radio>
                  </b-col>
                  <b-col md="2">
                    <b-form-radio
                      v-model="newCampaignData.budgetType"
                      name="repeat-radios"
                      value="TOTAL"
                      class="float-left"
                      @change="calculateTotal"
                    >
                      Total
                    </b-form-radio>
                    <feather-icon
                      v-b-tooltip.hover
                      icon="AlertCircleIcon"
                      size="12"
                      title="Automatically spacing the budget with system algorithms."
                      class="float-left ml-1"
                    />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="3">
                    <validation-provider
                      #default="{ errors }"
                      name="Execution method"
                      rules="required"
                    >
                      <b-form-group
                        :label="null"
                        label-for="selected-repeat"
                        :state="errors.length > 0 ? false : null"
                      >
                        <v-select
                          id="selected-repeat"
                          v-model="newCampaignData.budgetRunType"
                          :options="selectRepeat"
                          :reduce="(option) => option.value"
                          :clearable="false"
                          label="text"
                          class="mt-1"
                        />
                        <b-form-invalid-feedback
                          :state="errors.length > 0 ? false : null"
                        >
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col md="3">
                    <validation-provider
                      #default="{ errors }"
                      name="Budget"
                      rules="required"
                      :disabled="newCampaignData.noEndDate"
                    >
                      <b-input-group
                        :prepend="null"
                        :append="getCurrencyEmblem(adAccount.currency)"
                        class="input-group-merge mt-1"
                        :state="errors.length > 0 ? false : null"
                        :class="{ 'is-invalid': errors.length > 0 }"
                      >
                        <b-form-input
                          v-model="newCampaignData.budgetNumber"
                          type="number"
                          placeholder="500"
                          @keypress="checkNumber"
                          :class="{ 'is-invalid': errors.length > 0 }"
                        />
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-col>

                  <b-col md="6">
                    <div class="d-flex align-items-center">
                      <span class="ml-1">Expected Total</span>
                      <b-input-group class="input-group-merge ml-1">
                        <b-form-input
                          v-model="newCampaignData.expectedTotal"
                          placeholder="15000"
                          :disabled="newCampaignData.noEndDate"
                          readonly
                          :class="{
                            'bg-transparent': !newCampaignData.noEndDate,
                          }"
                        />
                        <b-input-group-append is-text>
                          {{ getCurrencyEmblem(adAccount.currency) }}
                        </b-input-group-append>
                      </b-input-group>
                    </div>
                  </b-col>

                  <b-col md="12" class="mt-1">
                    <h5>Proportion</h5>
                  </b-col>
                  <b-col md="6">
                    <validation-provider
                      #default="{ errors }"
                      name="Weekdays"
                      rules="required|numberCheckRule"
                      :disabled="newCampaignData.budgetType !== 'WEEKLY'"
                    >
                      <b-form-group label-for="weekdays">
                        Weekdays
                        <b-input-group
                          :prepend="null"
                          append="%"
                          class="input-group-merge"
                          :class="{
                            'is-invalid':
                              errors.length > 0 &&
                              newCampaignData.budgetType === 'WEEKLY',
                          }"
                        >
                          <b-form-input
                            id="weekdays"
                            v-model="newCampaignData.proportionWeekdays"
                            :disabled="newCampaignData.budgetType !== 'WEEKLY'"
                            :state="
                              newCampaignData.budgetType === 'WEEKLY' &&
                              errors.length > 0
                                ? false
                                : null
                            "
                            :class="{
                              'is-invalid':
                                errors.length > 0 &&
                                newCampaignData.budgetType === 'WEEKLY',
                            }"
                            type="number"
                            @keypress="checkNumber"
                          />
                        </b-input-group>
                        <small
                          class="text-danger"
                          v-if="newCampaignData.budgetType === 'WEEKLY'"
                        >
                          {{ errors[0] }}
                        </small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                  <b-col md="6">
                    <validation-provider
                      #default="{ errors }"
                      name="Weekeends"
                      rules="required"
                      :disabled="newCampaignData.budgetType !== 'WEEKLY'"
                    >
                      <b-form-group label-for="weekends">
                        Weekends
                        <b-input-group
                          :prepend="null"
                          append="%"
                          class="input-group-merge"
                          :state="
                            newCampaignData.budgetType === 'WEEKLY' &&
                            errors.length > 0
                              ? false
                              : null
                          "
                        >
                          <b-form-input
                            id="weekends"
                            v-model="newCampaignData.proportionWeekends"
                            :disabled="newCampaignData.budgetType !== 'WEEKLY'"
                            type="number"
                            readonly
                          />
                        </b-input-group>
                        <small
                          class="text-danger"
                          v-if="newCampaignData.budgetType === 'WEEKLY'"
                        >
                          {{ errors[0] }}
                        </small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
            <b-col md="12">
              <b-card>
                <b-row>
                  <b-col md="12">
                    <h5 class="mb-0">Bidding Type</h5>
                  </b-col>
                </b-row>
                <b-row class="mt-1">
                  <b-col md="3">
                    <b-form-radio
                      v-model="newCampaignData.biddingType"
                      name="bidding-radios"
                      value="OPEN_AUCTION"
                    >
                      Open Auction
                    </b-form-radio>
                  </b-col>
                  <b-col md="3">
                    <b-form-radio
                      v-model="newCampaignData.biddingType"
                      name="bidding-radios"
                      value="PMP_INCLUDE"
                    >
                      PMP Include
                    </b-form-radio>
                  </b-col>
                  <b-col md="3">
                    <b-form-radio
                      v-model="newCampaignData.biddingType"
                      name="bidding-radios"
                      value="PMP_ONLY"
                    >
                      PMP Only
                    </b-form-radio>
                  </b-col>
                </b-row>
                <b-row class="my-1">
                  <b-col md="12">
                    PMP
                    <feather-icon
                      variant="primary"
                      icon="PlusCircleIcon"
                      class="cursor-pointer ml-1"
                      size="18"
                      ref="btn"
                      @click="addPmp"
                    />
                  </b-col>
                </b-row>
                <b-form
                  ref="form"
                  class="repeater-form"
                  @submit.prevent="addPmp"
                >
                  <b-row
                    v-for="(item, index) in newCampaignData.pmpList"
                    :key="index"
                    ref="row"
                    class="d-flex align-items-center"
                  >
                    <b-col md="6">
                      <validation-provider
                        #default="{ errors }"
                        name="Deal ID"
                        rules="required"
                        :disabled="
                          newCampaignData.biddingType === 'OPEN_AUCTION'
                        "
                      >
                        <b-form-group label-for="deal-id" class="ml-2">
                          Deal ID
                          <b-form-input
                            v-model="item.dealId"
                            :disabled="
                              newCampaignData.biddingType === 'OPEN_AUCTION'
                            "
                            :state="
                              newCampaignData.biddingType !== 'OPEN_AUCTION' &&
                              errors.length > 0
                                ? false
                                : null
                            "
                          />
                          <small
                            class="text-danger"
                            v-if="
                              newCampaignData.biddingType !== 'OPEN_AUCTION'
                            "
                          >
                            {{ errors[0] }}
                          </small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                    <b-col md="5">
                      <validation-provider
                        #default="{ errors }"
                        name="Bid Price"
                        rules="required"
                        :disabled="
                          newCampaignData.biddingType === 'OPEN_AUCTION'
                        "
                      >
                        <b-form-group label-for="bid-price">
                          Bid Price
                          <b-form-input
                            v-model="item.price"
                            :disabled="
                              newCampaignData.biddingType === 'OPEN_AUCTION'
                            "
                            :state="
                              newCampaignData.biddingType !== 'OPEN_AUCTION' &&
                              errors.length > 0
                                ? false
                                : null
                            "
                            type="number"
                            @keypress="checkNumber"
                          />
                          <small
                            class="text-danger"
                            v-if="
                              newCampaignData.biddingType !== 'OPEN_AUCTION'
                            "
                          >
                            {{ errors[0] }}
                          </small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                    <b-col md="1">
                      <feather-icon
                        variant="primary"
                        icon="MinusCircleIcon"
                        class="cursor-pointer"
                        size="18"
                        ref="btn"
                        @click="removePmp(index)"
                      />
                    </b-col>
                  </b-row>
                </b-form>
              </b-card>
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>

      <!-- Step 4: Review tab -->
      <tab-content title="Review">
        <validation-observer ref="socialRules" tag="form">
          <b-row>
            <b-col cols="12" class="mb-2">
              <h3 class="mb-0">Step 4</h3>
              <span class="text-muted">Campaign Creation Review</span>
            </b-col>
            <b-col md="12">
              <b-card>
                <b-row>
                  <b-col md="4">Campaign Goal</b-col>
                  <b-col md="8">
                    <span class="mr-3">{{ newCampaignData.goal }}</span> |
                    <span class="ml-3">
                      {{
                        newCampaignData.includeLat
                          ? "LAT Include " +
                            newCampaignData.includeLatProportion +
                            " %"
                          : "LAT Exclude"
                      }}
                    </span>
                  </b-col>
                </b-row>
                <hr />
                <b-row class="mt-1">
                  <b-col md="4"> Title </b-col>
                  <b-col md="8">
                    {{ newCampaignData.title }}
                  </b-col>
                </b-row>
                <b-row class="mt-1" v-if="newCampaignData.description">
                  <b-col md="4"> Description </b-col>
                  <b-col md="8">
                    {{ newCampaignData.description }}
                  </b-col>
                </b-row>
                <b-row class="mt-1">
                  <b-col md="4"> Country </b-col>
                  <b-col md="8">
                    {{ newCampaignData.targetCountry }}
                  </b-col>
                </b-row>
                <b-row class="mt-1">
                  <b-col md="4">Domain</b-col>
                  <b-col md="8">
                    {{ newCampaignData.advertiserDomain }}
                  </b-col>
                </b-row>
                <b-row class="mt-1">
                  <b-col md="4">Android</b-col>
                  <b-col md="8">
                    {{ newCampaignData.appAndroid }}
                  </b-col>
                </b-row>
                <b-row class="mt-1">
                  <b-col md="4">IOS</b-col>
                  <b-col md="8">
                    {{ newCampaignData.appIos }}
                  </b-col>
                </b-row>
                <hr />
                <b-row class="mt-1">
                  <b-col md="4"> Schedule </b-col>
                  <b-col md="8">
                    <template v-if="newCampaignData.noEndDate">
                      {{ newCampaignData.scheduleStartDate }}
                    </template>
                    <template v-else>
                      {{ newCampaignData.scheduleStartDate }} ~
                      {{ newCampaignData.scheduleEndDate }}
                    </template>
                  </b-col>
                </b-row>
                <b-row class="mt-1">
                  <b-col md="4">Budget</b-col>
                  <b-col md="8">
                    <span class="mr-1">
                      {{ newCampaignData.budgetType }} -
                      {{ newCampaignData.budgetRunType }}
                      <span style="margin-left: 16px"></span>
                      {{ getCurrencyEmblem(adAccount.currency)

                      }}{{ newCampaignData.budgetNumber }}
                    </span>
                  </b-col>
                </b-row>
                <b-row class="mt-1">
                  <b-col md="4"></b-col>
                  <b-col md="8">
                    <template v-if="!newCampaignData.noEndDate">
                      <span class="mr-1">
                        Expected Total
                        <span style="margin-left: 10px"></span>
                        {{ getCurrencyEmblem(adAccount.currency)

                        }}{{ newCampaignData.expectedTotal }}
                      </span>
                    </template>
                  </b-col>
                </b-row>
                <b-row
                  class="mt-1"
                  v-if="newCampaignData.budgetType === 'WEEKLY'"
                >
                  <b-col md="4"> Proportion </b-col>
                  <b-col md="8">
                    <span class="mr-1">
                      Weekdays : {{ newCampaignData.proportionWeekdays }} %
                    </span>
                    |
                    <span class="ml-1">
                      Weekends : {{ newCampaignData.proportionWeekends }} %
                    </span>
                  </b-col>
                </b-row>
                <hr />
                <b-row class="mt-1">
                  <b-col md="4"> Bidding Type </b-col>
                  <b-col md="8">
                    {{ newCampaignData.biddingType }}
                  </b-col>
                </b-row>
                <b-row
                  class="mt-1"
                  v-if="newCampaignData.biddingType !== 'OPEN_AUCTION'"
                >
                  <b-col md="4"> PMP </b-col>
                  <b-col md="8">
                    <div
                      v-for="(item, index) in newCampaignData.pmpList"
                      :key="'id-' + index"
                    >
                      <span class="mr-2">Deal ID : {{ item.dealId }}</span> |
                      <span class="ml-2">
                        Bid Price :
                        {{ getCurrencyEmblem(adAccount.currency)

                        }}{{ item.price }}
                      </span>
                      <br />
                    </div>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>
    </form-wizard>
  </b-modal>
</template>

<script>
import { FormWizard, TabContent } from "vue-form-wizard";
import vSelect from "vue-select";
import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import moment from "moment";

import {
  VBTooltip,
  BCard,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormCheckbox,
  BInputGroup,
  BFormInvalidFeedback,
  BInputGroupPrepend,
  BInputGroupAppend,
  BFormTextarea,
} from "bootstrap-vue";
import FlatPickr from "vue-flatpickr-component";
import { required } from "@validations";

import CommonService from "@/services/common.service";
import CampaignService from "@/services/campaign.service";
import AdAccountService from "@/services/ad-account.service";
import TrackingLinkService from "@/services/mmp-tracking.service";

extend("appCheckRule", {
  params: ["android", "ios"],
  validate(value, { android, ios }) {
    return ios != "" || android != "";
  },
  computesRequired: true,
  message: "The App Bundle fields is required",
});
extend("numberCheckRule", {
  validate(value) {
    return value >= 0 && value <= 100;
  },
  message: "Only numbers from 0 to 100 can be entered.",
});

export default {
  name: "CreateCampaignModal",
  components: {
    ValidationProvider,
    ValidationObserver,
    FormWizard,
    TabContent,
    BCard,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormCheckbox,
    BInputGroup,
    BFormInvalidFeedback,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormTextarea,
    ToastificationContent,
    vSelect,
    FlatPickr,
    moment,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    id: {
      type: Number,
      default: null,
    },
    mode: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      adAccount: {},

      campaignTitle: "Create Campaign",
      newCampaignData: {
        goal: "CPM",
        includeLat: false,
        includeLatProportion: "",
        title: "",
        description: "",
        selectedTargetCountry: [],
        targetCountry: "",
        advertiserDomain: "",
        app: false,
        appAndroid: "",
        appIos: "",
        scheduleStartDate: "",
        scheduleEndDate: "",
        noEndDate: false,
        budgetType: "DAILY",
        selectedBudgetRunType: [],
        budgetRunType: "",
        budgetNumber: 0,
        expectedTotal: 0,
        proportionWeekdays: "",
        proportionWeekends: "",
        biddingType: "OPEN_AUCTION",
        adAccountId: 1,
        pmpList: [
          {
            id: "",
            campaignId: "",
            dealId: "",
            price: "",
          },
        ],
        campId: "",
        trkLinkCampLvParam: "",
        trkLinkSq: ""
      },
      startDateConfig: {
        altFormat: "M j, Y",
        altInput: true,
        minDate: this.mode !== null ? "" : new Date(),
        maxDate: "",
      },
      endDateConfig: {
        altFormat: "M j, Y",
        altInput: true,
        minDate: "",
      },
      required,
      countries: [],
      selectRepeat: [
        { value: "FIXED", text: "Fixed" },
        { value: "AVERAGED", text: "Averaged" },
      ],
      titleLength: 50,
      descriptionLength: 200,

      mmpTrkLinks: [],
    };
  },
  watch: {
    "newCampaignData.includeLat": function (newVal) {
      if (!newVal) {
        this.newCampaignData.includeLatProportion = "";
      }
    },
    "newCampaignData.app": function (newVal) {
      if (!newVal) {
        this.newCampaignData.appAndroid = "";
        this.newCampaignData.appIos = "";
      }
    },
    "newCampaignData.noEndDate": function (newVal) {
      if (newVal) {
        this.newCampaignData.scheduleEndDate = "";
        this.newCampaignData.expectedTotal = 0;
      }
    },
    "newCampaignData.biddingType": function (newVal) {
      if (newVal === "OPEN_AUCTION") {
        this.newCampaignData.pmpList = [
          {
            id: "",
            campaignId: "",
            dealId: "",
            price: "",
          },
        ];
      }
    },
    "newCampaignData.scheduleStartDate": function (newVal) {
      this.endDateConfig.minDate = newVal;
      this.calculateTotal();
    },
    "newCampaignData.scheduleEndDate": function (newVal) {
      this.startDateConfig.maxDate = newVal;
      this.calculateTotal();
    },
    "newCampaignData.budgetNumber": function () {
      this.calculateTotal();
    },
    "newCampaignData.proportionWeekdays": function (newVal) {
      if (newVal <= 100 && newVal >= 0) {
        this.newCampaignData.proportionWeekends = 100 - newVal;
      }
    },
    "newCampaignData.budgetType": function (newVal) {
      if (newVal !== "WEEKLY") {
        this.newCampaignData.proportionWeekdays = "";
      }
    },
  },
  methods: {
    async loadAdAccount() {
      const response = await AdAccountService.getOne(this.$store.getters.getSelectedAdAccount);
      if (response && response.data.success) {
        this.adAccount = response.data.data;
        this.newCampaignData.adAccountId = response.data.data.id;
      }
    },
    checkNumber(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    calculateTotal() {
      if (this.newCampaignData.noEndDate) return;
      if (this.newCampaignData.scheduleEndDate === "") return;
      if (this.newCampaignData.budgetNumber === "") return;
      const stDt = moment(new Date(this.newCampaignData.scheduleStartDate));
      const edDt = moment(new Date(this.newCampaignData.scheduleEndDate));
      const diffDt = edDt.diff(stDt, "days") + 1;
      const calDt =
        this.newCampaignData.budgetType !== "WEEKLY" ? diffDt : diffDt / 7;
      if (this.newCampaignData.budgetType === "TOTAL") {
        this.newCampaignData.expectedTotal = this.newCampaignData.budgetNumber;
      } else {
        this.newCampaignData.expectedTotal = (
          this.newCampaignData.budgetNumber * calDt
        ).toFixed(2);
      }
    },
    inputGoal() {
      if (this.newCampaignData.includeLat) {
        return new Promise((resolve, reject) => {
          this.$refs.goalRules.validate().then((success) => {
            if (success) {
              resolve(true);
            } else {
              reject();
            }
          });
        });
      } else {
        return true;
      }
    },
    inputBasicInfo() {
      return new Promise((resolve, reject) => {
        this.$refs.infoRules.validate().then((success) => {
          if (success) {
            resolve(true);
          } else {
            reject();
          }
        });
      });
    },
    async inputSchedule() {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          this.$refs.scheduleRules.validate().then((success) => {
            if (success) {
              resolve(true);
            } else {
              reject();
            }
          });
        });
      });
    },
    addPmp() {
      if (this.newCampaignData.biddingType === "OPEN_AUCTION") return;
      this.newCampaignData.pmpList.push({
        id: "",
        campaignId: "",
        dealId: "",
        price: "",
      });
    },
    removePmp(index) {
      this.newCampaignData.pmpList.splice(index, 1);
    },
    async formSubmitted() {
      if (this.mode === "edit") this.newCampaignData.campId = this.id;
      if (this.newCampaignData.biddingType === "OPEN_AUCTION")
        this.newCampaignData.pmpList = [];
      const swalText = this.mode === null ? "create" : this.mode;
      this.$swal({
        title: "Are you sure?",
        text: `You want to ${swalText} the campaign!`,
        icon: "warning",
        showCancelButton: true,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response =
            this.mode === "edit"
              ? await CampaignService.update(this.newCampaignData)
              : await CampaignService.create(this.newCampaignData);
          if (response && response.status === 200) {
            this.$bvModal.hide("create-campaign-modal");
            this.$toast({
              component: ToastificationContent,
              props: {
                title: `Successfully ${swalText}!`,
                icon: "EditIcon",
                variant: "success",
              },
            });
            this.$root.$emit("reload-campaign-list");
            this.$root.$emit("reload-adaccount");
            this.$root.$emit("reload-campaign-nav");
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: response.data.message || "Unknown error",
                icon: "AlertCircleIcon",
                variant: "danger",
              },
            });
          }
        }
      });
    },
    async loadCountries() {
      const countryResponse = await CommonService.getCountries({});
      if (countryResponse && countryResponse.data.success) {
        this.countries = countryResponse.data.data;
      }
    },
    async loadCampaign() {
      const response = await CampaignService.get(this.id);

      if (response && response.data.success) {
        this.newCampaignData = response.data.data;

        if (this.mode === "edit") {
          this.campaignTitle = "Edit Campaign";
        } else {
          this.campaignTitle = "Copy Campaign";
          this.newCampaignData.title = this.truncate(this.newCampaignData.title, 45) + "_COPY";
        }

        this.newCampaignData.trkLinkCampLvParam = response.data.data.trkLinkCampLvParam == null ? "" : response.data.data.trkLinkCampLvParam;
      }
    },
    async loadTrackingLinks() {
      const response = await TrackingLinkService.list(null);

      if (response && response.data.success) {
        this.mmpTrkLinks = response.data.data;
      }
    },
    truncate(str, n) {
      return str.length > n ? str.slice(0, n) : str;
    },
  },
  async created() {
    this.loadCountries();
    this.loadTrackingLinks();

    if (this.id !== null) {
      this.loadCampaign();
    }

    this.loadAdAccount();
  },
};
</script>

<style>

.vue-form-wizard .wizard-header {
  display: none;
}

.modal-lg,
.modal-xl {
  max-width: 1100px;
}

</style>
